import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { Alert } from '../../components/ui/Alert';
import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { ApiError, api } from '../../lib/api';
import { pollUntil, uploadAvatar } from '../../lib/upload';
import { useSession } from '../../stores/session';

type Phase = 'idle' | 'uploading' | 'processing' | 'done';

/**
 * Choose, upload, and wait for a new profile picture.
 *
 * ## This is the rehearsal for track upload
 *
 * The four states below are the same four a track upload has, for the same
 * reasons (HANDOFF §4.1), and they are worth getting right here where the file
 * is 200KB rather than 200MB:
 *
 *   **uploading** - bytes going straight to object storage, never through the
 *   API. Needs a real progress bar, which is why `uploadDirect` uses XHR.
 *
 *   **processing** - the API has accepted it, and a worker is doing the actual
 *   work. This is the state people find confusing: the upload finished, and the
 *   thing they uploaded is not there yet. Saying so explicitly is most of the
 *   fix. For audio this phase is minutes, not seconds.
 *
 *   **done** - the derived files exist.
 *
 *   **failed** - which can happen *after* a successful upload, because the
 *   worker rejects things the browser could not check.
 *
 * The wait is a poll. Phase 4 replaces it with a `track.ready` event on
 * `private-user.{id}` (§8.1) and this component keeps its shape.
 */
export function AvatarPicker({ currentUrl, displayName }: { currentUrl?: string | null; displayName: string }) {
  const input = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const user = useSession((s) => s.user);
  const refreshUser = useSession((s) => s.refreshUser);

  const [phase, setPhase] = useState<Phase>('idle');
  const [percent, setPercent] = useState(0);
  const [preview, setPreview] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  async function choose(file: File) {
    setFailure(null);
    setPercent(0);

    /*
     * A local preview, shown the instant a file is chosen.
     *
     * The upload and the processing behind it take long enough that without
     * this the page looks inert while something is plainly happening. It is
     * also honest: this really is the image they picked.
     */
    setPreview(URL.createObjectURL(file));
    setPhase('uploading');

    try {
      await uploadAvatar(file, { onProgress: setPercent });

      setPhase('processing');

      /*
       * The API said yes; the worker has not finished. Poll the profile until
       * the derived sizes appear.
       *
       * Compared against the URL we started with, so this cannot be satisfied
       * by the *previous* avatar still being there - which is what makes it
       * work for somebody replacing a picture rather than setting a first one.
       */
      const ready = await pollUntil(
        () => api.get<{ avatar: { medium: string } | null }>(`/users/${user?.username}`),
        (profile) => Boolean(profile.avatar) && profile.avatar?.medium !== currentUrl,
      );

      if (!ready) {
        setFailure('Still processing. It should appear shortly - try reloading in a moment.');
        setPhase('idle');

        return;
      }

      setPhase('done');
      await refreshUser();
      await queryClient.invalidateQueries({ queryKey: ['profile'] });
    } catch (error) {
      /*
       * Two quite different failures land here and the messages have to differ.
       *
       * A 422 is the API refusing the file - wrong type, too large - and its
       * message names the actual problem. Anything else is the PUT to storage
       * failing, which for a cross-origin request is very often CORS, and the
       * browser deliberately tells script nothing useful about it.
       */
      setFailure(
        error instanceof ApiError
          ? error.fieldErrors.content_type ?? error.fieldErrors.size_bytes ?? error.message
          : (error as Error).message,
      );
      setPhase('idle');
      setPreview(null);
    }
  }

  const busy = phase === 'uploading' || phase === 'processing';

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-4">
        <Avatar src={preview ?? currentUrl} name={displayName} size={72} />

        <div className="flex flex-col gap-1.5">
          <Button variant="secondary" disabled={busy} onClick={() => input.current?.click()}>
            {currentUrl ? 'Change picture' : 'Upload a picture'}
          </Button>
          <span className="text-xs text-faint">JPEG, PNG, WebP or GIF. Up to 8MB.</span>
        </div>

        <input
          ref={input}
          type="file"
          // The native control is hidden behind a styled button, but it is still
          // a real input - so keyboard and screen-reader users get the system
          // file picker rather than a div pretending to be one.
          className="sr-only"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={(event) => {
            const file = event.target.files?.[0];

            // Reset, so choosing the same file twice after a failure still
            // fires a change event.
            event.target.value = '';

            if (file) void choose(file);
          }}
        />
      </div>

      {phase === 'uploading' && (
        <div className="flex flex-col gap-1">
          <div className="h-1.5 overflow-hidden rounded-full bg-raised">
            <div
              className="h-full rounded-full bg-accent transition-[width] duration-150"
              style={{ width: `${percent}%` }}
            />
          </div>
          <span className="text-xs text-faint">Uploading… {percent}%</span>
        </div>
      )}

      {phase === 'processing' && (
        <span className="text-xs text-faint">
          {/* Named explicitly. "Upload complete but the image is not there yet"
              is the confusing moment, and for audio it lasts minutes. */}
          Uploaded. Resizing…
        </span>
      )}

      {phase === 'done' && <Alert tone="success">Picture updated.</Alert>}
      {failure && <Alert tone="error">{failure}</Alert>}
    </div>
  );
}
